import { useMemo } from 'react';
import type { Version } from '@/types/GraphqlSchema';
import { snakeToCamel } from '@/utils/helpers';

export type FieldChange = {
  fieldName: string;
  oldValue: string | number | null;
  newValue: string | number | null;
  changedAt: string;
  changedBy: string;
  userId: string;
};

export type FieldVersionHistory = {
  latestChange: FieldChange | null;
  allChanges: FieldChange[];
};

/**
 * Hook to extract and process version history for form fields
 * @param versions - Array of PaperTrail versions from GraphQL
 * @returns Map of field names to their version history
 */
export const useVersionHistory = (
  versions?: Version[] | null,
): Map<string, FieldVersionHistory> => {
  return useMemo(() => {
    const fieldHistoryMap = new Map<string, FieldVersionHistory>();

    if (!versions || versions.length === 0) {
      return fieldHistoryMap;
    }

    versions.forEach(version => {
      if (!version.objectChanges) return;

      const changes = version.objectChanges as Record<
        string,
        [string | number | null, string | number | null]
      >;
      const userName = version.user
        ? `${version.user.firstName || ''} ${version.user.lastName || ''}`.trim() ||
          version.user.email
        : 'Unknown User';
      const userId = version.user?.id || 'unknown';

      Object.entries(changes).forEach(([snakeCaseFieldName, [oldValue, newValue]]) => {
        const fieldName = snakeToCamel(snakeCaseFieldName);

        const fieldChange: FieldChange = {
          fieldName,
          oldValue,
          newValue,
          changedAt: version.createdAt,
          changedBy: userName,
          userId,
        };

        if (!fieldHistoryMap.has(fieldName)) {
          fieldHistoryMap.set(fieldName, {
            latestChange: fieldChange,
            allChanges: [fieldChange],
          });
        } else {
          const history = fieldHistoryMap.get(fieldName);
          if (history) {
            history.allChanges.push(fieldChange);
          }
        }
      });
    });

    return fieldHistoryMap;
  }, [versions]);
};
